"use client";

/**
 * Cart contents + checkout form — rendered by the Cart Section block
 * (`section.cart`, see CartSection.tsx). Reads the gift stored by the /gift
 * PDP from the gift-cart cookie (src/lib/giftCart.ts) and renders its
 * display snapshot as the product row — fully dynamic, no CMS cards on
 * this section — then collects the gift details.
 *
 * Submit POSTs same-origin to `/api/gift-checkout` (a thin server proxy to
 * the app's `/api/billing/gift-checkout`, which owns validation + Polar +
 * the gifts table) and redirects to the returned Polar checkout URL. The
 * recipient email is typed twice on purpose — typo'd recipient emails are
 * the #1 gift-flow failure mode. The cookie is cleared only after checkout
 * actually starts (navigation), so a failed attempt keeps the cart.
 *
 * Every string is editable via the block's Form Copy tab (`copy` prop);
 * empty CMS values fall back to DEFAULT_COPY.
 */

import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Button } from "@/components/button/button/button";
import ProductCardHorizontal from "@/components/cards/productCardHorizontal/productCardHorizontal";
import { Input } from "@/components/form/input/input";
import { TextArea } from "@/components/form/textArea/textArea";
import { Label } from "@/components/label/label";
import { Separator } from "@/components/separator/separator";
import { captureEvent } from "@/lib/analytics";
import {
  clearGiftCart,
  parseGiftCart,
  readGiftCart,
  readGiftCartRaw,
  type GiftCartItem,
} from "@/lib/giftCart";
import { cn } from "@/lib/utils";
import styles from "./CartForm.module.css";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MESSAGE_MAX_LENGTH = 500;
const SENDER_NAME_MAX_LENGTH = 100;

/** All CMS-editable cart strings. Empty/missing values fall back to
 *  DEFAULT_COPY, so the page never renders a blank label. */
export interface CartCopy {
  emptyCartText?: string | null;
  emptyCartLinkLabel?: string | null;
  formTitle?: string | null;
  recipientEmailLabel?: string | null;
  recipientEmailPlaceholder?: string | null;
  recipientEmailHelper?: string | null;
  confirmEmailLabel?: string | null;
  confirmEmailPlaceholder?: string | null;
  confirmEmailHelper?: string | null;
  senderNameLabel?: string | null;
  senderNamePlaceholder?: string | null;
  senderNameHelper?: string | null;
  messageLabel?: string | null;
  messagePlaceholder?: string | null;
  messageHelper?: string | null;
  buyerEmailLabel?: string | null;
  buyerEmailPlaceholder?: string | null;
  buyerEmailHelper?: string | null;
  submitLabel?: string | null;
  submitBusyLabel?: string | null;
  finePrint?: string | null;
}

/** Built-in fallbacks — kept in sync with the block's `defaultValue`s in
 *  src/payload/blocks/CartSection.ts. */
const DEFAULT_COPY = {
  emptyCartText: "Your cart is empty.",
  emptyCartLinkLabel: "Choose a gift",
  formTitle: "Gift details",
  recipientEmailLabel: "Who is it for? (their email)",
  recipientEmailPlaceholder: "recipient@example.com",
  confirmEmailLabel: "Confirm their email",
  confirmEmailPlaceholder: "Type it again to be safe",
  senderNameLabel: "From (your name, shown to them)",
  senderNamePlaceholder: "Grandma June",
  messageLabel: "Gift message (optional)",
  messagePlaceholder: "Happy birthday! I thought you'd enjoy learning this.",
  buyerEmailLabel: "Your email (for your receipt)",
  buyerEmailPlaceholder: "you@example.com",
  submitLabel: "Checkout",
  submitBusyLabel: "Starting checkout…",
  finePrint:
    "You'll pay securely through Polar and get an emailed receipt. We email the gift to them right after, or you can share the claim link yourself. Their time starts when they claim, and the gift never auto-renews.",
} as const;

function withDefault(value: string | null | undefined, fallback: string): string {
  const v = (value ?? "").trim();
  return v || fallback;
}

/** Helpers have no fallback — empty means no helper line. */
function optionalText(value: string | null | undefined): string | undefined {
  const v = (value ?? "").trim();
  return v || undefined;
}

export interface CartFormProps {
  /** CMS-editable strings; anything empty falls back to DEFAULT_COPY. */
  copy?: CartCopy;
  /** Storybook/test override: bypasses the cookie read. */
  initialItem?: GiftCartItem | null;
}

// The cookie never notifies; re-reads happen via re-renders (removal flips
// state). A stable no-op subscription keeps useSyncExternalStore happy.
const emptySubscribe = () => () => {};

const CartForm = ({ copy, initialItem }: CartFormProps) => {
  const router = useRouter();
  // Cookie is browser-only. useSyncExternalStore renders the server
  // snapshot (undefined = "resolving") during SSR/hydration, then the real
  // cookie value — no hydration mismatch, no setState-in-effect. The
  // snapshot is the raw cookie STRING (a stable primitive); the object is
  // memoized off it, since an unstable object snapshot would loop renders.
  const rawCookie = React.useSyncExternalStore(
    emptySubscribe,
    readGiftCartRaw,
    () => undefined,
  );
  const cookieItem = React.useMemo(
    () => (rawCookie === undefined ? undefined : parseGiftCart(rawCookie)),
    [rawCookie],
  );
  const item: GiftCartItem | null | undefined =
    initialItem !== undefined ? initialItem : cookieItem;

  const [recipientEmail, setRecipientEmail] = React.useState("");
  const [recipientEmailConfirm, setRecipientEmailConfirm] = React.useState("");
  const [senderName, setSenderName] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [buyerEmail, setBuyerEmail] = React.useState("");
  // Field errors only surface after a submit attempt — red text while the
  // buyer is still typing reads as scolding.
  const [attempted, setAttempted] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (initialItem !== undefined) return; // stories/tests
    captureEvent("gift_cart_viewed", { has_item: !!readGiftCart() });
  }, [initialItem]);

  // An empty cart never lingers: send the visitor (back) to the PDP. Guarded
  // off `busy` so the deliberate cookie clear when checkout starts can't
  // race a redirect against the Polar navigation. The empty-state markup
  // below stays as the interstitial/fallback while the redirect happens.
  const busyRef = React.useRef(false);
  React.useEffect(() => {
    if (initialItem !== undefined) return; // stories/tests keep the empty state visible
    if (item === null && !busyRef.current) router.replace("/gift");
  }, [item, initialItem, router]);

  // Still resolving the cookie — render nothing for a flicker-free settle.
  if (item === undefined) return null;

  // Empty cart: no (valid) cookie. Visitors are redirected to /gift by the
  // effect above; this renders as the interstitial (and the fallback if
  // client navigation is unavailable — the button links there too).
  if (!item) {
    return (
      <div className={styles.empty}>
        <p className={cn("body-md", "muted", styles.emptyText)}>
          {withDefault(copy?.emptyCartText, DEFAULT_COPY.emptyCartText)}
        </p>
        <Button variant="primary" size="lg" asChild>
          <Link href="/gift">
            {withDefault(copy?.emptyCartLinkLabel, DEFAULT_COPY.emptyCartLinkLabel)}
          </Link>
        </Button>
      </div>
    );
  }

  const buyerEmailValid = EMAIL_RE.test(buyerEmail.trim());
  const recipientEmailValid = EMAIL_RE.test(recipientEmail.trim());
  const recipientMatches =
    recipientEmail.trim().toLowerCase() === recipientEmailConfirm.trim().toLowerCase();
  const formValid = buyerEmailValid && recipientEmailValid && recipientMatches;

  const fieldError = !attempted
    ? null
    : !recipientEmailValid
      ? "Enter a valid email for the person you're gifting to."
      : !recipientMatches
        ? "The recipient emails don't match."
        : !buyerEmailValid
          ? "Enter a valid email for your receipt."
          : null;

  const handleRemove = () => {
    clearGiftCart();
    captureEvent("gift_cart_item_removed", { polar_product_id: item.polarProductId });
    // Straight back to the product page to pick again.
    router.push("/gift");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (!formValid || busy) return;
    setBusy(true);
    busyRef.current = true;
    setServerError(null);
    captureEvent("gift_form_submitted", { polar_product_id: item.polarProductId });
    try {
      const res = await fetch("/api/gift-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          polarProductId: item.polarProductId,
          buyerEmail: buyerEmail.trim(),
          recipientEmail: recipientEmail.trim(),
          senderName: senderName.trim(),
          message: message.trim(),
        }),
      });
      const data = (await res.json().catch(() => null)) as
        | { checkoutUrl?: string; error?: string }
        | null;
      if (!res.ok || !data?.checkoutUrl) {
        setServerError(
          data?.error ?? "Something went wrong starting checkout. Please try again.",
        );
        setBusy(false);
        busyRef.current = false;
        return;
      }
      // Checkout is genuinely starting — the cart's job is done.
      clearGiftCart();
      window.location.assign(data.checkoutUrl);
      // Keep the busy state — we're navigating away to Polar.
    } catch {
      setServerError("Something went wrong starting checkout. Please try again.");
      setBusy(false);
      busyRef.current = false;
    }
  };

  const messageHelper = optionalText(copy?.messageHelper);

  return (
    <form className={styles.root} onSubmit={handleSubmit} noValidate>
      <ProductCardHorizontal
        title={item.planTitle || "Gift package"}
        subtitle={item.planSubtitle}
        priceText={item.price}
        strikeoutPrice={item.strikeoutPrice}
        onRemove={busy ? undefined : handleRemove}
      />

      <div className={styles.formDivider}>
        <Separator />
      </div>
      <h3 className={cn("title-sm", styles.formTitle)}>
        {withDefault(copy?.formTitle, DEFAULT_COPY.formTitle)}
      </h3>

      <div className={styles.fields}>
        <Input
          size="lg"
          type="email"
          label={withDefault(copy?.recipientEmailLabel, DEFAULT_COPY.recipientEmailLabel)}
          helperText={optionalText(copy?.recipientEmailHelper)}
          autoComplete="off"
          placeholder={withDefault(
            copy?.recipientEmailPlaceholder,
            DEFAULT_COPY.recipientEmailPlaceholder,
          )}
          value={recipientEmail}
          disabled={busy}
          onChange={(e) => setRecipientEmail(e.target.value)}
        />
        <Input
          size="lg"
          type="email"
          label={withDefault(copy?.confirmEmailLabel, DEFAULT_COPY.confirmEmailLabel)}
          helperText={optionalText(copy?.confirmEmailHelper)}
          autoComplete="off"
          placeholder={withDefault(
            copy?.confirmEmailPlaceholder,
            DEFAULT_COPY.confirmEmailPlaceholder,
          )}
          value={recipientEmailConfirm}
          disabled={busy}
          onChange={(e) => setRecipientEmailConfirm(e.target.value)}
        />
        <Input
          size="lg"
          type="text"
          label={withDefault(copy?.senderNameLabel, DEFAULT_COPY.senderNameLabel)}
          helperText={optionalText(copy?.senderNameHelper)}
          placeholder={withDefault(
            copy?.senderNamePlaceholder,
            DEFAULT_COPY.senderNamePlaceholder,
          )}
          maxLength={SENDER_NAME_MAX_LENGTH}
          value={senderName}
          disabled={busy}
          onChange={(e) => setSenderName(e.target.value)}
        />
        <div>
          <Label htmlFor="cart-gift-message" size="md" className={styles.messageLabel}>
            {withDefault(copy?.messageLabel, DEFAULT_COPY.messageLabel)}
            {messageHelper ? (
              <span className={styles.helperLine}>{messageHelper}</span>
            ) : null}
          </Label>
          <TextArea
            id="cart-gift-message"
            size="md"
            rows={4}
            resize="vertical"
            placeholder={withDefault(
              copy?.messagePlaceholder,
              DEFAULT_COPY.messagePlaceholder,
            )}
            maxLength={MESSAGE_MAX_LENGTH}
            value={message}
            disabled={busy}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        <Input
          size="lg"
          type="email"
          label={withDefault(copy?.buyerEmailLabel, DEFAULT_COPY.buyerEmailLabel)}
          helperText={optionalText(copy?.buyerEmailHelper)}
          autoComplete="email"
          placeholder={withDefault(
            copy?.buyerEmailPlaceholder,
            DEFAULT_COPY.buyerEmailPlaceholder,
          )}
          value={buyerEmail}
          disabled={busy}
          onChange={(e) => setBuyerEmail(e.target.value)}
        />
      </div>

      {fieldError || serverError ? (
        <p className={cn("body-sm", styles.errorLine)}>{fieldError ?? serverError}</p>
      ) : null}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        className={styles.submit}
        disabled={busy}
      >
        {busy
          ? withDefault(copy?.submitBusyLabel, DEFAULT_COPY.submitBusyLabel)
          : withDefault(copy?.submitLabel, DEFAULT_COPY.submitLabel)}
      </Button>

      <p className={cn("body-sm", "muted", styles.finePrint)}>
        {withDefault(copy?.finePrint, DEFAULT_COPY.finePrint)}
      </p>
    </form>
  );
};

export default CartForm;
